/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import { motion } from "framer-motion";
import React from "react";
import { FaClock } from "react-icons/fa";
import { useRecoilState } from "recoil";
import { CampaingBuilderFormAtom } from "../../../../atoms/CampaingBuilderFormAtom";
import { IDelayTimerConstraints } from "../../../../generated/Api";
import useMediaQuery from "../../../../hook/useMediaQuery";
import { INodeTypes } from "../../../../types/CampaignTypes";

interface ContainerProps {
  error: boolean;
  isFormOpen?: boolean;
}

const Container = styled(motion.div)<ContainerProps>`
  width: ${(props) => (props.isFormOpen ? "82px" : "240px")};
  min-height: ${(props) => (props.isFormOpen ? "82px" : "fit-content")};
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #dddddd;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: ${(props) => (props.isFormOpen ? "center" : "start")};
  cursor: pointer;

  svg {
    width: ${(props) => (props.isFormOpen ? "40px" : "16px")};
    height: ${(props) => (props.isFormOpen ? "40px" : "16px")};
  }

  ${(props) =>
    props.error &&
    css`
      box-shadow: 0px 0px 6px #ff0000;
      border: 1px solid #ff0000;
    `}

  @media screen and (min-width: 1080px) {
    width: 240px;
    min-height: fit-content;
    justify-content: start;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-size: 16px;
  font-weight: 600;

  display: flex;
  align-items: center;
  justify-content: center;

  > svg {
    margin-right: 4px;
  }
`;

const DateWrapper = styled.div`
  color: #2f2f2f;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  text-align: center;

  margin-top: 8px;

  white-space: pre;
`;

interface Props {
  onClick: () => any;
  time: number;
  unit: number;
  constraints?: IDelayTimerConstraints;
  error: boolean;
  type: INodeTypes;
}

function getDate(time: number, unit: number) {
  switch (unit) {
    case 1:
      return time.toFixed() + ` minute${time.toFixed() > "1" ? "s" : ""}`;
    case 2:
      return time.toFixed() + ` hour${time.toFixed() > "1" ? "s" : ""}`;
    case 3:
      return time.toFixed() + ` day${time.toFixed() > "1" ? "s" : ""}`;
    case 4:
      return time.toFixed() + ` week${time.toFixed() > "1" ? "s" : ""}`;
    case 5:
      return time.toFixed() + ` month${time.toFixed() > "1" ? "s" : ""}`;
  }
}

const WeekMap = {
  "0": "Sunday",
  "1": "Monday",
  "2": "Tuesday",
  "3": "Wednesday",
  "4": "Thursday",
  "5": "Friday",
  "6": "Saturday",
};

export default function DelayCard({
  onClick,
  time,
  unit,
  constraints,
  error,
  type,
}: Props) {
  let isEveryWeekend = false;
  let isEveryWeekDay = false;
  let isEveryDay = false;
  const [form, setForm] = useRecoilState(CampaingBuilderFormAtom);
  let constraintText = "";
  const isLargerThan1080 = useMediaQuery("(min-width: 1080px)");

  if (constraints) {
    const { weekDays, time: constraintTime } = constraints;
    isEveryWeekend =
      weekDays.includes(0) &&
      !weekDays.includes(1) &&
      !weekDays.includes(2) &&
      !weekDays.includes(3) &&
      !weekDays.includes(4) &&
      !weekDays.includes(5) &&
      weekDays.includes(6);

    isEveryWeekDay =
      !weekDays.includes(0) &&
      weekDays.includes(1) &&
      weekDays.includes(2) &&
      weekDays.includes(3) &&
      weekDays.includes(4) &&
      weekDays.includes(5) &&
      !weekDays.includes(6);

    isEveryDay =
      weekDays.includes(0) &&
      weekDays.includes(1) &&
      weekDays.includes(2) &&
      weekDays.includes(3) &&
      weekDays.includes(4) &&
      weekDays.includes(5) &&
      weekDays.includes(6);

    let timeOffset = dayjs.utc(constraintTime);
    let today = dayjs.utc();
    today = today.utc().set("hours", timeOffset.utc().get("hours"));
    today = today.utc().set("minutes", timeOffset.utc().get("minutes"));
    today = today.utc().set("seconds", timeOffset.utc().get("seconds"));
    today = today
      .utc()
      .set("milliseconds", timeOffset.utc().get("milliseconds"));

    let constraintTimeFormatted = dayjs(today).local().format("h:mm a");

    if (type === INodeTypes.Email) {
      if (isEveryWeekend) {
        constraintText = `Send email on the\nweekend at ${constraintTimeFormatted}`;
      } else if (isEveryWeekDay) {
        constraintText = `Send email on the weekdays\n at ${constraintTimeFormatted}`;
      } else if (isEveryDay) {
        constraintText = `Send email on any day\n at ${constraintTimeFormatted}`;
      } else if (weekDays.length > 0) {
        constraintText = `Send email on ${weekDays
          .filter(
            (_, i) =>
              // If there's only 1, don't filter the last one out
              i != (weekDays.length > 1 ? weekDays.length - 1 : weekDays.length)
          )
          .map((wd) => (WeekMap as any)[wd])
          .join(",\n")} ${
          weekDays.length > 1
            ? `and ${(WeekMap as any)[weekDays[weekDays.length - 1]]}`
            : ""
        }\n at ${constraintTimeFormatted}`;
      } else {
        constraintText = `Select day`;
      }
    } else {
      if (isEveryWeekend) {
        constraintText = `Apply or remove tag(s) on the\nweekend at ${constraintTimeFormatted}`;
      } else if (isEveryWeekDay) {
        constraintText = `Apply or remove tag(s) on the weekdays\n at ${constraintTimeFormatted}`;
      } else if (isEveryDay) {
        constraintText = `Apply or remove tag(s) every day\n at ${constraintTimeFormatted}`;
      } else if (weekDays.length > 0) {
        constraintText = `Apply or remove tag(s)\non ${weekDays
          .filter(
            (_, i) =>
              // If there's only 1, don't filter the last one out
              i != (weekDays.length > 1 ? weekDays.length - 1 : weekDays.length)
          )
          .map((wd) => (WeekMap as any)[wd])
          .join(",\n")} ${
          weekDays.length > 1
            ? `and ${(WeekMap as any)[weekDays[weekDays.length - 1]]}`
            : ""
        }\n at ${constraintTimeFormatted}`;
      } else {
        constraintText = `Select day`;
      }
    }
  }

  return (
    <Container
      isFormOpen={form !== null}
      layout
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      error={error}
      onClick={onClick}
    >
      {isLargerThan1080 || form === null ? (
        <>
          <Title style={{ textTransform: "unset" }}>
            <FaClock color="#FB972E" />
            {getDate(time, unit) === "1 minute"
              ? `Wait at least ${getDate(time, unit)}`
              : `Wait for ${getDate(time, unit)}`}
          </Title>
          {constraints && <DateWrapper>{constraintText}</DateWrapper>}
        </>
      ) : (
        <FaClock color="#FB972E" />
      )}
    </Container>
  );
}
